import { FormEvent } from "react"
import { Head, router, useForm } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

type InvitationRow = {
  id: number
  email: string
  status: "pending" | "accepted" | "expired" | "revoked"
  invited_by_email: string
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
  last_sent_at: string | null
  created_at: string
  pending: boolean
}

type Props = {
  invitations: InvitationRow[]
  one_time_invitation_url?: string | null
  email_delivery_configured: boolean
  expiration_hours: number
}

export default function AdminInvitationsIndex({ invitations, one_time_invitation_url, email_delivery_configured, expiration_hours }: Props) {
  const form = useForm({ user_invitation: { email: "" } })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post("/admin/invitations", { preserveScroll: true, onSuccess: () => form.reset() })
  }

  return (
    <>
      <Head title="Invitations | LeadFlow">
        <meta name="description" content="Create and manage LeadFlow invitations." />
        <meta property="og:title" content="Invitations | LeadFlow" />
        <meta property="og:description" content="Create and manage LeadFlow invitations." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1>Invitations</h1>
              <p className="mt-1">Invite new users with single-use links that expire after {expiration_hours} hours.</p>
            </div>
          </div>
        </div>

        {one_time_invitation_url && (
          <section className="mt-6 rounded-md border border-accent/30 bg-accent-faded p-4">
            <h2 className="text-base font-semibold text-ink-display">Copy invitation link</h2>
            <p className="mt-1 text-sm text-ink-muted">This link includes the raw token and is shown only once.</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input readOnly value={one_time_invitation_url} aria-label="One-time invitation link" onFocus={(event) => event.currentTarget.select()} />
              <Button type="button" variant="secondary" onClick={() => navigator.clipboard?.writeText(one_time_invitation_url)}>Copy</Button>
            </div>
          </section>
        )}

        <section className="mt-6 rounded-md border border-hairline bg-page p-4">
          <h2 className="text-lg font-semibold text-ink-display">Create invitation</h2>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <label htmlFor="invitation-email">Email</label>
              <Input id="invitation-email" type="email" autoComplete="email" required value={form.data.user_invitation.email} onChange={(event) => form.setData("user_invitation", { email: event.target.value })} />
            </div>
            <Button type="submit" disabled={form.processing}>Generate invitation</Button>
          </form>
          {!email_delivery_configured && <p className="mt-3 text-sm text-ink-muted">Email delivery is not configured for production invitations. Use the copy link action after generating an invitation.</p>}
        </section>

        <div className="mt-6 overflow-hidden rounded-md border border-hairline bg-page">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Invited by</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {invitations.map((invitation) => (
                <tr key={invitation.id}>
                  <td className="px-4 py-3 font-medium text-ink-display">{invitation.email}</td>
                  <td className="px-4 py-3"><StatusBadge status={invitation.status} /></td>
                  <td className="px-4 py-3 text-ink-muted">{invitation.invited_by_email}</td>
                  <td className="px-4 py-3 text-ink-muted">{formatDate(invitation.expires_at)}</td>
                  <td className="px-4 py-3">
                    {invitation.pending ? (
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="secondary" onClick={() => router.post(`/admin/invitations/${invitation.id}/regenerate`, {}, { preserveScroll: true })}>Regenerate</Button>
                        <Button type="button" variant="secondary" onClick={() => router.patch(`/admin/invitations/${invitation.id}/revoke`, {}, { preserveScroll: true })}>Revoke</Button>
                      </div>
                    ) : (
                      <span className="text-ink-muted">No actions</span>
                    )}
                  </td>
                </tr>
              ))}
              {invitations.length === 0 && <tr><td className="px-4 py-6 text-center text-ink-muted" colSpan={5}>No invitations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </AdminShell>
    </>
  )
}

function StatusBadge({ status }: { status: InvitationRow["status"] }) {
  const tone = status === "pending" ? "accent" : status === "accepted" ? "signal" : status === "revoked" ? "signal" : "muted"
  return <Badge tone={tone}>{status}</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}
